import { useMemo } from 'react';
import { useSecuritiesContext } from '../contexts';
import type { Security } from '../types/Security';
import { getSearchTerms, isMultileg } from '../utils';
import { createSecuritySearchRecord, searchSecurityRecords } from './searchableSecurityRecords';

type UseSearchableSecurityListProps = {
  search: string;
  securitiesList?: Security[];
  searchOnDescription?: boolean;
  omitMultileg?: boolean;
  includeSedol?: boolean;
};

const MIN_MATCH_CHAR_LENGTH = 2;

export function useSearchableSecurityList({
  search,
  securitiesList: propSecuritiesList,
  searchOnDescription = true,
  omitMultileg = false,
  includeSedol = false,
}: UseSearchableSecurityListProps): Security[] {
  // Using sorted securities will be beneficial in ties, since index is used in that scenario.
  const { securitiesListSortedByRank } = useSecuritiesContext();
  const securitiesList = useMemo(() => {
    const list = propSecuritiesList ?? securitiesListSortedByRank;
    if (omitMultileg) {
      return list.filter(sec => !isMultileg(sec));
    }
    return list;
  }, [securitiesListSortedByRank, propSecuritiesList, omitMultileg]);

  const searchRecords = useMemo(
    () => securitiesList.map(security => createSecuritySearchRecord(security, searchOnDescription, includeSedol)),
    [includeSedol, searchOnDescription, securitiesList]
  );

  const searchResults = useMemo(() => {
    const terms = getSearchTerms(search, MIN_MATCH_CHAR_LENGTH);
    return searchSecurityRecords(searchRecords, terms);
  }, [search, searchRecords]);

  return searchResults;
}
